/* Lakū Skin Quiz — state machine + routing
 * Generated 2026-04-26 from laku-quiz-spec.md + laku-quiz-result-variants.md.
 * Source-of-truth: phase1-content/specialist (FAQ corpus uses same routing primitives).
 *
 * Lifecycle:
 *  1. boot()  — wire up DOM, render Q1
 *  2. answer(qid, optionId[]) — record + advance
 *  3. captureEmail({email, phone, consent}) — POST to Klaviyo (when key present) or queue
 *  4. routeToVariant() — pure function over state -> variant id
 *  5. renderResult(variantId)
 *  6. addAllToCart(variantId) — POST to /cart/add.js (Shopify native)
 */
(function () {
  "use strict";
  if (window.__lakuQuizBooted) return;
  window.__lakuQuizBooted = true;

  // ---- DATA ---------------------------------------------------------
  const QUESTIONS = [
    {
      id: "Q1", type: "single", title: "What's your #1 skin concern right now?",
      intro: "Let's start with what brought you here. One honest answer.",
      options: [
        { id: "A", label: "Dark spots from old breakouts" },
        { id: "B", label: "Larger patches (forehead, cheeks, upper lip)" },
        { id: "C", label: "Dark body areas (underarms, knees, bikini line)" },
        { id: "D", label: "My skin feels damaged or reactive" },
        { id: "E", label: "Ingrown hair or razor bump marks" },
        { id: "F", label: "I want to prevent future dark spots" },
      ],
    },
    {
      id: "Q2", type: "single", title: "How long have you been dealing with this?",
      intro: "This helps me set honest expectations for your Fade.",
      options: [
        { id: "A", label: "A few weeks" },
        { id: "B", label: "A few months" },
        { id: "C", label: "Over a year" },
        { id: "D", label: "Honestly? As long as I can remember" },
      ],
    },
    {
      id: "Q3", type: "multi", title: "Where is the pigmentation? (Pick all that apply.)",
      intro: "Face, body, or both? No wrong answer.",
      options: [
        { id: "A", label: "Full face" },
        { id: "B", label: "Forehead / cheeks (patches)" },
        { id: "C", label: "Around mouth / upper lip" },
        { id: "D", label: "Jawline / neck" },
        { id: "E", label: "Underarms" },
        { id: "F", label: "Bikini line / inner thighs" },
        { id: "G", label: "Knees / elbows / knuckles" },
        { id: "H", label: "Back / chest" },
      ],
    },
    {
      id: "Q4", type: "multi", title: "What have you already tried? (Pick all that apply — no shame.)",
      intro: "I need to know where you're coming from. Honesty only makes the plan better.",
      options: [
        { id: "A", label: "Hydroquinone or prescription bleaching cream" },
        { id: "B", label: "Vitamin C serums" },
        { id: "C", label: "Niacinamide" },
        { id: "D", label: "Retinol or Differin" },
        { id: "E", label: "Chemical peels or at-home acid treatments" },
        { id: "F", label: "Natural/DIY (turmeric, lemon, yogurt)" },
        { id: "G", label: "Other brands (Topicals, The Ordinary, etc.)" },
        { id: "H", label: "Nothing yet — I'm starting fresh" },
      ],
    },
    {
      id: "Q5", type: "single", title: "How does your skin feel most days?",
      intro: "This tells me about your barrier — the foundation of everything.",
      options: [
        { id: "A", label: "Tight, dry, or ashy" },
        { id: "B", label: "Oily, especially T-zone" },
        { id: "C", label: "Combination — oily middle, dry edges" },
        { id: "D", label: "Sensitive — burns or stings easily" },
        { id: "E", label: "Normal, just discolored" },
      ],
    },
    {
      id: "Q6", type: "single", title: "How often do you wear sunscreen?",
      intro: "I'll explain why this matters so much in a minute.",
      options: [
        { id: "A", label: "Every single day" },
        { id: "B", label: "Only when I'm outside a lot" },
        { id: "C", label: "Rarely or never" },
        { id: "D", label: "I've never found one that doesn't leave a cast" },
      ],
    },
    {
      id: "Q7", type: "single", title: "Are you pregnant, postpartum, or on hormonal birth control?",
      intro: "This affects which products are safe for you right now — and flags if your concern might be melasma.",
      options: [
        { id: "A", label: "Pregnant" },
        { id: "B", label: "Postpartum or nursing" },
        { id: "C", label: "On hormonal birth control" },
        { id: "D", label: "None of these" },
        { id: "E", label: "Prefer not to say" },
      ],
    },
    {
      id: "Q8", type: "single", title: "How fast are you hoping to see results — honestly?",
      intro: "I'd rather tell you the truth than sell you a miracle.",
      options: [
        { id: "A", label: "A few weeks" },
        { id: "B", label: "A couple of months" },
        { id: "C", label: "I know this is a long game, I'm in it" },
        { id: "D", label: "I just want to stop making it worse" },
      ],
    },
    {
      id: "Q9", type: "single", title: "What's your budget comfort for a full routine?",
      intro: "Honest pricing — I'll match the plan to your range.",
      options: [
        { id: "A", label: "Under $50 — start me small" },
        { id: "B", label: "$50–$100" },
        { id: "C", label: "$100+ — give me the full stack" },
        { id: "D", label: "One product to start, I'll build up" },
      ],
    },
  ];

  // ---- VARIANT COPY (8 + 1 fallback) -------------------------------
  // Sourced from laku-quiz-result-variants.md. Product handles map to live Shopify.
  const VARIANTS = {
    V1: {
      id: "V1",
      eyebrow: "Your Fade Plan",
      headline: "Post-Breakout Fade Path",
      whatsHappening: [
        "Those dark marks aren't scars — they're post-inflammatory hyperpigmentation. Melanin-rich skin responds to inflammation, like old breakouts, by making extra pigment, and the mark sticks around long after the pimple is gone.",
        "Good news: it fades. The same pigment response that makes the marks stubborn is the same reason they're temporary — it's color, not damage. Consistency with the right routine, plus daily sunscreen, is how The Fade happens.",
      ],
      timeline: [
        { when: "30 days", text: "barrier stable, overall tone slightly more even" },
        { when: "60 days", text: "early signs of The Fade on lighter marks" },
        { when: "90 days", text: "deeper marks noticeably lighter, real glow" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Your daily cleanser — face + body" },
        { handle: "turmeric-face-cream", note: "The hero of the Lakū Routine" },
        { handle: "radiance-spf60", note: "Non-negotiable — makes everything else work" },
      ],
      stopDoing: "Picking is the #1 way dark marks get worse. Every pick triggers more inflammation, more pigment. Pimple patches help — physically AND psychologically. Try them.",
    },
    V2: {
      id: "V2",
      eyebrow: "Your Fade Plan",
      headline: "Barrier Rebuild + Fade",
      whatsHappening: [
        "Your barrier is stressed. Before we talk about fading anything, we need to let your skin settle. Sensitive, stinging, tight — that's your skin telling you it's reactive right now, and pushing actives on a reactive barrier makes hyperpigmentation worse on melanin-rich skin, not better.",
        "The next 30 days are about protection, not treatment. Once your skin feels comfortable again — not stinging, not tight, not reacting — we layer in the actives that help The Fade.",
      ],
      timeline: [
        { when: "30 days", text: "stinging stops, redness gone, skin feels settled" },
        { when: "60 days", text: "add Face Cream's full benefit, early Fade starts" },
        { when: "90 days", text: "barrier strong, routine fully built, fade underway" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Your gentle daily cleanser" },
        { handle: "turmeric-face-cream", note: "Barrier support first, fade support second" },
        { handle: "radiance-spf60", note: "Morning protection, every single day" },
      ],
      stopDoing: "Pause everything else. No scrubs, no peels, no retinol, no vitamin C while your skin is reactive. If you're using any of those, back off for 30 days — just cleanse, moisturize, and protect. Let your skin tell you when it's ready for more.",
    },
    V3: {
      id: "V3",
      eyebrow: "Your Fade Plan",
      headline: "Hormonal Patch Plan",
      whatsHappening: [
        "What you're describing sounds like melasma — larger, symmetric patches usually on forehead, cheeks, or upper lip. Melasma has a hormonal trigger (pregnancy, birth control, sometimes just genetics) and a sun trigger — which is why it's stubborn and tends to come back.",
        "I'll tell you the truth: melasma is patient work. It doesn't respond the way post-breakout marks do. We manage it, we fade the appearance over time, and we protect against it coming back with aggressive daily sunscreen. Not a quick story. But absolutely doable.",
      ],
      timeline: [
        { when: "30 days", text: "daily sun habit established (most important step)" },
        { when: "90 days", text: "slight softening of patch edges" },
        { when: "6 months", text: "visible fade possible with perfect sun protection" },
        { when: "Ongoing", text: "melasma is managed, not cured — sunscreen forever" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Your daily cleanser" },
        { handle: "turmeric-face-cream", note: "Apply to full face, not just patches" },
        { handle: "radiance-spf60", note: "Melasma's worst enemy — wear it every day" },
      ],
      stopDoing: "Sunscreen is the whole game for melasma. No sunscreen = no fade, ever. Reapply at lunch if near a window. Heat itself can worsen melasma — hot yoga, saunas, steam rooms can trigger flares. If you're pregnant or nursing, talk to your OB before starting any new active.",
    },
    V4: {
      id: "V4",
      eyebrow: "Your Fade Plan",
      headline: "Body Fade Routine",
      whatsHappening: [
        "Body hyperpigmentation is so common on melanin-rich skin, and almost nobody formulates for it. It happens in high-friction areas (knees, elbows, underarms, bikini line, inner thighs) because friction triggers melanin production.",
        "The approach is the same logic as face PIH, just bigger real estate. Consistency wins. Patience wins. Body dark spots are slower to fade than face marks because body skin is thicker. Expect 8–12 weeks before you see the needle move.",
      ],
      timeline: [
        { when: "30 days", text: "skin feels softer, hydration baseline restored" },
        { when: "60 days", text: "rough-feeling patches smooth out, appearance evens slightly" },
        { when: "90+ days", text: "visible Fade on high-friction areas" },
        { when: "6 months", text: "full glow-up possible with consistency" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Use in the shower on dark body areas" },
        { handle: "turmeric-body-oil", note: "After every shower, on damp skin" },
        { handle: "turmeric-knuckle-cream", note: "Targeted on the darkest patches" },
      ],
      stopDoing: "Aggressive exfoliation makes body PIH worse. Skip dermabrasion tools and harsh scrubs. Hibiscus Scrub once a week is plenty. Razor technique matters — sharp blade, with the grain, moisturize after. Dull blade + dry shave = ingrown inflammation = more pigment.",
    },
    V5: {
      id: "V5",
      eyebrow: "Your Fade Plan",
      headline: "Underarm Brighten Plan",
      whatsHappening: [
        "Dark underarms are one of the most common concerns I get asked about, and it's almost never what people think. It's usually friction from clothing, shaving irritation, deodorant ingredients (aluminum, alcohol), and your skin's protective melanin response. Sometimes also dead skin buildup that reads as darkness.",
        "The fix is multi-step but not complicated. Gentler cleanse, smarter shaving, softer deodorant while skin settles, consistent body oil at night. Give it 6–8 weeks. It works.",
      ],
      timeline: [
        { when: "30 days", text: "texture improves, feels softer and less dry" },
        { when: "60 days", text: "first visible brightening" },
        { when: "90 days", text: "noticeable evening of tone" },
        { when: "4 months+", text: "the underarms you've been wanting" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Use on underarms — stop using harsh body washes here" },
        { handle: "turmeric-body-oil", note: "Apply at night, after shower" },
      ],
      stopDoing: "Switch your deodorant — at least temporarily — to aluminum-free or gentle. Aluminum + friction is irritating your underarms every day. Sharp razor, with the grain, moisturize after. If you wax or use hair-removal cream, wait 24 hours before any active product on freshly-treated skin.",
    },
    V6: {
      id: "V6",
      eyebrow: "Your Fade Plan",
      headline: "Razor Mark Fade",
      whatsHappening: [
        "Ingrown hair marks are the same type of dark mark as post-breakout spots — post-inflammatory hyperpigmentation from irritation. Every ingrown, every razor bump, every picked-at bump leaves pigment behind. If you're in a constant cycle of shave → ingrown → heal → dark mark → shave again, the pigment builds up.",
        "Breaking the cycle is two parts: prevent the inflammation (better shaving, don't pick), and fade the existing marks. Both matter. You can't just fade — you have to stop making new ones.",
      ],
      timeline: [
        { when: "30 days", text: "skin feels calmer, fewer new bumps if shaving technique is fixed" },
        { when: "60 days", text: "older marks start lightening" },
        { when: "90–120 days", text: "visible Fade across most marked areas" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Use before and after shaving" },
        { handle: "turmeric-body-oil", note: "Apply to marks and shaved areas daily" },
        { handle: "turmeric-knuckle-cream", note: "Use on the most stubborn marks" },
      ],
      stopDoing: "Stop picking at ingrowns. Every squeeze + pick extends the inflammation. Fix your shave: sharp fresh blade, WITH the direction of hair growth, moisturize immediately after. Never dry shave. Don't use alcohol-based aftershaves on marked skin.",
    },
    V7: {
      id: "V7",
      eyebrow: "Your Fade Plan",
      headline: "The Maintenance Routine",
      whatsHappening: [
        "Love this question — because prevention is so much easier than fading. Melanin-rich skin has two main pigmentation triggers: sun exposure and inflammation. Daily sunscreen and a gentle anti-inflammatory routine handles both.",
        "Your routine is simpler than someone actively fading marks. Keep it consistent, don't skip the SPF, be gentle with your skin — and if a breakout or irritation happens, start The Fade routine early instead of waiting for marks to stick around.",
      ],
      timeline: [
        { when: "30 days", text: "daily sunscreen habit locked in, skin more even-toned baseline" },
        { when: "90 days", text: "glow-up noticeable, skin texture smoothing" },
        { when: "Ongoing", text: "prevention pays compound interest — your 40-year-old self will thank you" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Your everyday cleanser" },
        { handle: "turmeric-face-cream", note: "Gentle protection with natural brighteners" },
        { handle: "radiance-spf60", note: "The most important product in your routine, full stop" },
      ],
      stopDoing: "Don't skip sunscreen because you 'didn't go outside.' UV still comes through windows. Don't chase trends — retinol, acids, peels, vitamin C have their place but aren't maintenance essentials for your skin right now. Consistency beats complexity.",
    },
    V8: {
      id: "V8",
      eyebrow: "Your Fade Plan",
      headline: "Barrier First Reset — 30 Days",
      whatsHappening: [
        "Here's what I want you to hear first: you're not ruined. Your skin can come back.",
        "What's happening is your barrier is compromised. Long-term strong actives — hydroquinone, aggressive peels, stacked retinoids — can damage the barrier on melanin-rich skin, and sometimes create the opposite of what you wanted (paradoxical darkening, reactive patches, blue-grey cast in some cases).",
        "The next 30 days are a reset. No actives. No fade attempts. Just cleanse, moisturize, protect. Let your skin settle before we do anything else. If what you're seeing has a blue-grey tint, please talk to a dermatologist about tapering the prescription you're on. Lakū can't replace that conversation — but we can help you rebuild once you're ready.",
      ],
      timeline: [
        { when: "Days 1–14", text: "cleanse + moisturize only. No actives. No scrubs. No sun without SPF." },
        { when: "Days 15–30", text: "add daily SPF. Skin starts settling. Less stinging." },
        { when: "Day 31+", text: "if your barrier is calm, we talk about adding one active back slowly" },
      ],
      routine: [
        { handle: "turmeric-gold-foil-soap", note: "Your gentle reset cleanser" },
        { handle: "turmeric-face-cream", note: "Pure barrier support for 30 days" },
        { handle: "radiance-spf60", note: "Add from day 15+" },
      ],
      stopDoing: "Do not layer additional actives for 30 days. No retinol, no vitamin C, no acids, no exfoliants, no peels. Talk to your dermatologist about your hydroquinone — long-term use has been linked to paradoxical darkening (ochronosis). Pause picking, squeezing, and physical manipulation while it heals.",
    },
  };

  // ---- ROUTING ------------------------------------------------------
  // Pure function: state -> variant id. Order matters (first match wins).
  function routeToVariant(state) {
    const a = state.answers;
    const has = (q, ...ids) => ids.some(id => (a[q] || []).includes(id));

    // Priority 1 — Hard reset trigger (hydroquinone or aggressive peels)
    if (has("Q4", "A") || (has("Q4", "E") && !has("Q4", "B", "C", "D", "G"))) return "V8";

    // Priority 2 — Sensitive barrier trumps fade routing
    if (has("Q5", "D") && (has("Q1", "A") || has("Q1", "B"))) return "V2";

    // Priority 3 — Melasma signals (large patches OR hormonal context with face concern)
    if (has("Q1", "B")) return "V3";
    if ((has("Q7", "A") || has("Q7", "B") || has("Q7", "C")) && (has("Q1", "A") || has("Q1", "B"))) return "V3";

    // Priority 4 — Body-only routing
    if (has("Q1", "C")) {
      // underarms primary?
      const bodyAreas = (a.Q3 || []).filter(id => ["E","F","G","H"].includes(id));
      if (bodyAreas.length === 1 && bodyAreas[0] === "E") return "V5";
      return "V4";
    }

    // Priority 5 — Razor / ingrown
    if (has("Q1", "E")) return "V6";

    // Priority 6 — Prevention
    if (has("Q1", "F")) return "V7";

    // Priority 7 — Damaged/reactive
    if (has("Q1", "D")) return "V2";

    // Default — post-breakout fade
    return "V1";
  }

  // ---- KLAVIYO TAGS (for analytics + segmentation) ------------------
  function buildKlaviyoTags(state) {
    const a = state.answers, tags = ["quiz_completed"];
    const v = routeToVariant(state);
    tags.push("variant_" + v);
    if (a.Q1?.[0]) tags.push("primary_concern_Q1_" + a.Q1[0]);
    if (a.Q5?.[0]) tags.push("barrier_status_Q5_" + a.Q5[0]);
    if (a.Q6?.[0]) tags.push("spf_user_Q6_" + a.Q6[0]);
    if (a.Q8?.[0]) tags.push("timeline_expectation_Q8_" + a.Q8[0]);
    if (a.Q9?.[0]) tags.push("budget_tier_Q9_" + a.Q9[0]);
    if ((a.Q4 || []).includes("A")) tags.push("hydroquinone_user");
    if ((a.Q7 || []).some(id => id === "A" || id === "B")) tags.push("pregnancy_postpartum");
    return tags;
  }

  // ---- STATE --------------------------------------------------------
  const state = {
    step: 0, // 0..8 questions, 9 = email, 10 = result
    answers: {}, // {Q1: ["A"], Q3: ["A","B"], ...}
    email: null, phone: null,
    productCatalog: null, // populated lazily from Shopify products.json
  };

  function persist() {
    try { localStorage.setItem("laku_quiz_state", JSON.stringify(state)); } catch(_) {}
  }
  function restore() {
    try {
      const raw = localStorage.getItem("laku_quiz_state");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      Object.assign(state, parsed);
    } catch(_) {}
  }

  // ---- DOM HELPERS --------------------------------------------------
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const h = (tag, attrs, kids) => {
    const el = document.createElement(tag);
    if (attrs) for (const [k,v] of Object.entries(attrs)) {
      if (k === "class") el.className = v;
      else if (k === "html") el.innerHTML = v;
      else if (k.startsWith("on") && typeof v === "function") el.addEventListener(k.slice(2), v);
      else if (v != null) el.setAttribute(k, v);
    }
    if (kids) (Array.isArray(kids) ? kids : [kids]).forEach(k => {
      if (k == null || k === false) return;
      el.appendChild(typeof k === "string" ? document.createTextNode(k) : k);
    });
    return el;
  };

  // ---- RENDERERS ----------------------------------------------------
  let root = null;

  function renderProgress() {
    const total = QUESTIONS.length + 1; // 9 Q + email
    const cur = Math.min(state.step + 1, total);
    return h("div", { class: "lq-progress" }, [
      h("div", { class: "lq-progress__bar", style: `width: ${(cur/total)*100}%` }),
      h("span", { class: "lq-progress__label" }, `${cur} of ${total}`),
    ]);
  }

  function renderQuestion(idx) {
    const q = QUESTIONS[idx];
    const selected = new Set(state.answers[q.id] || []);
    const opts = q.options.map(o => {
      const on = selected.has(o.id);
      return h("button", {
        type: "button",
        class: "lq-option" + (on ? " is-selected" : ""),
        "data-id": o.id,
        "aria-pressed": on ? "true" : "false",
        onclick: () => toggleOption(q, o.id),
      }, [
        h("span", { class: "lq-option__radio", "aria-hidden": "true" }),
        h("span", { class: "lq-option__label" }, o.label),
        h("span", { class: "lq-option__check", "aria-hidden": "true" }, on ? "✓" : ""),
      ]);
    });
    return h("section", { class: "lq-screen lq-screen--question", "data-q": q.id }, [
      renderProgress(),
      h("p", { class: "lq-intro" }, q.intro),
      h("h2", { class: "lq-title" }, q.title),
      q.type === "multi" ? h("p", { class: "lq-hint" }, "Pick all that apply.") : null,
      h("div", { class: "lq-options" }, opts),
      h("div", { class: "lq-actions" }, [
        idx > 0 ? h("button", { type: "button", class: "lq-btn lq-btn--ghost", onclick: back }, "← Back") : null,
        h("button", {
          type: "button",
          class: "lq-btn lq-btn--primary",
          disabled: selected.size === 0 ? "" : null,
          onclick: () => next(q),
        }, idx === QUESTIONS.length - 1 ? "Get my plan →" : "Next →"),
      ]),
    ]);
  }

  function renderEmail() {
    return h("section", { class: "lq-screen lq-screen--email" }, [
      renderProgress(),
      h("h2", { class: "lq-title" }, "You're all set, Radiance Doll."),
      h("p", { class: "lq-intro" }, "Your Fade Plan is ready. I'll send it to your inbox so you always have it — plus occasional updates on products, routines, and real Radiance Doll stories."),
      h("form", { class: "lq-form", onsubmit: submitEmail }, [
        h("label", { class: "lq-field" }, [
          h("span", { class: "lq-field__label" }, "Where should I send it?"),
          h("input", { type: "email", name: "email", required: "", placeholder: "you@example.com", autocomplete: "email" }),
        ]),
        h("label", { class: "lq-field" }, [
          h("span", { class: "lq-field__label" }, "Phone (optional, for SMS skin tips)"),
          h("input", { type: "tel", name: "phone", placeholder: "+1 (555) 123-4567", autocomplete: "tel" }),
        ]),
        h("label", { class: "lq-consent" }, [
          h("input", { type: "checkbox", name: "consent", required: "" }),
          h("span", null, "I agree to receive marketing emails from Lakū. Unsubscribe anytime."),
        ]),
        h("div", { class: "lq-actions" }, [
          h("button", { type: "button", class: "lq-btn lq-btn--ghost", onclick: back }, "← Back"),
          h("button", { type: "submit", class: "lq-btn lq-btn--primary" }, "Get My Plan →"),
          h("button", { type: "button", class: "lq-btn lq-btn--link", onclick: skipEmail }, "Skip — just show me my plan"),
        ]),
      ]),
      h("p", { class: "lq-fineprint" }, "Your email is safe. We don't sell your info."),
    ]);
  }

  function renderResult() {
    const variantId = routeToVariant(state);
    const v = VARIANTS[variantId];
    if (!v) return h("p", null, "Sorry — your result couldn't be loaded.");

    const cards = v.routine.map(r => h("li", { class: "lq-result__product", "data-handle": r.handle }, [
      h("img", { class: "lq-result__product-img", src: `/products/${r.handle}.json`, alt: "", "data-handle": r.handle, loading: "lazy" }),
      h("div", { class: "lq-result__product-body" }, [
        h("h4", { class: "lq-result__product-name" }, "Loading…"),
        h("p", { class: "lq-result__product-note" }, r.note),
        h("p", { class: "lq-result__product-price", "data-price": "" }, ""),
      ]),
    ]));

    const timeline = v.timeline.map(t => h("li", { class: "lq-result__timeline-item" }, [
      h("strong", { class: "lq-result__timeline-when" }, t.when),
      h("span", { class: "lq-result__timeline-text" }, t.text),
    ]));

    const node = h("section", { class: "lq-screen lq-screen--result", "data-variant": variantId }, [
      h("p", { class: "lq-result__eyebrow" }, v.eyebrow),
      h("h2", { class: "lq-result__headline" }, v.headline),
      h("section", { class: "lq-result__block" }, [
        h("h3", { class: "lq-result__h3" }, "What's actually happening"),
        ...v.whatsHappening.map(p => h("p", null, p)),
        h("p", { class: "lq-result__signoff" }, "— Hally, Certified Skincare Formulator & Repair Specialist"),
      ]),
      h("section", { class: "lq-result__block" }, [
        h("h3", { class: "lq-result__h3" }, "Your timeline"),
        h("ul", { class: "lq-result__timeline" }, timeline),
      ]),
      h("section", { class: "lq-result__block" }, [
        h("h3", { class: "lq-result__h3" }, "Your Lakū routine"),
        h("ol", { class: "lq-result__products" }, cards),
        h("div", { class: "lq-result__cta-row" }, [
          h("button", {
            type: "button",
            class: "lq-btn lq-btn--primary lq-result__cta-add",
            onclick: () => addAllToCart(variantId),
          }, "Add my routine to cart →"),
          h("a", { class: "lq-btn lq-btn--ghost", href: "/pages/contact" }, "Ask Hally a question"),
        ]),
      ]),
      v.stopDoing ? h("section", { class: "lq-result__block lq-result__stop" }, [
        h("h3", { class: "lq-result__h3" }, "What to stop doing"),
        h("p", null, v.stopDoing),
      ]) : null,
      h("p", { class: "lq-disclaimer" }, "Individual results may vary. Lakū is a cosmetic line — not a medical treatment. For medical skin concerns (persistent irritation, pain, spreading rash, or sudden skin changes), please consult a dermatologist."),
      h("button", { type: "button", class: "lq-btn lq-btn--link lq-result__retake", onclick: retake }, "Retake the quiz"),
    ]);

    // hydrate product cards with live data
    setTimeout(() => hydrateProducts(node, v.routine), 0);
    return node;
  }

  // ---- ACTIONS ------------------------------------------------------
  function toggleOption(q, optId) {
    const cur = new Set(state.answers[q.id] || []);
    if (q.type === "single") {
      cur.clear(); cur.add(optId);
    } else {
      cur.has(optId) ? cur.delete(optId) : cur.add(optId);
    }
    state.answers[q.id] = [...cur];
    persist(); render();
  }

  function next(q) {
    if (!state.answers[q.id] || state.answers[q.id].length === 0) return;
    state.step++; persist(); render(); scrollToTop();
  }
  function back() {
    if (state.step > 0) { state.step--; persist(); render(); scrollToTop(); }
  }
  function retake() {
    state.step = 0; state.answers = {}; state.email = null; state.phone = null;
    persist(); render(); scrollToTop();
  }
  function scrollToTop() {
    const el = root || document.documentElement;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function submitEmail(ev) {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    state.email = fd.get("email");
    state.phone = fd.get("phone") || null;

    const tags = buildKlaviyoTags(state);

    // Klaviyo identify (no-op if KLAVIYO_PUBLIC_KEY not set in window.lakuConfig)
    const klaviyoKey = (window.lakuConfig && window.lakuConfig.klaviyoPublicKey) || null;
    if (klaviyoKey && state.email) {
      try {
        await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${klaviyoKey}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "revision": "2024-10-15" },
          body: JSON.stringify({
            data: {
              type: "profile",
              attributes: {
                email: state.email,
                phone_number: state.phone,
                properties: { quiz_variant: routeToVariant(state), quiz_tags: tags, quiz_answers: state.answers },
              },
            },
          }),
        });
      } catch (e) { console.warn("[laku-quiz] klaviyo submit failed:", e); }
      // Also subscribe to the Welcome List so the quiz triggers the welcome flow (LAKU10)
      if (window.lakuConfig && window.lakuConfig.welcomeListId && window.lakuSubscribeWelcome) {
        /* Read the result. This used to discard it, so when Klaviyo rejected
         * every one of these calls with a 400 the quiz carried on promising
         * "10% off when you finish" and nothing was ever sent. */
        try {
          const subscribed = await window.lakuSubscribeWelcome(state.email, "Skin Quiz");
          if (!subscribed) {
            console.error("[laku-quiz] NOT subscribed to the welcome list — the 10% code will not be sent for", state.email);
          }
        } catch (e) {
          console.error("[laku-quiz] welcome subscribe threw:", e);
        }
      } else {
        console.error("[laku-quiz] welcome subscribe unavailable — no 10% code will be sent");
      }
    } else {
      // queue locally for next session — script using KLAVIYO_API_KEY can drain this
      try {
        const queue = JSON.parse(localStorage.getItem("laku_quiz_queue") || "[]");
        queue.push({ at: Date.now(), email: state.email, phone: state.phone, variant: routeToVariant(state), tags, answers: state.answers });
        localStorage.setItem("laku_quiz_queue", JSON.stringify(queue));
      } catch(_) {}
    }
    state.step = 10; persist(); render(); scrollToTop();
  }
  function skipEmail() { state.step = 10; persist(); render(); scrollToTop(); }

  async function addAllToCart(variantId) {
    const v = VARIANTS[variantId];
    const items = [];
    for (const r of v.routine) {
      try {
        const res = await fetch(`/products/${r.handle}.js`);
        if (!res.ok) continue;
        const product = await res.json();
        const variant = product.variants.find(x => x.available) || product.variants[0];
        if (variant) items.push({ id: variant.id, quantity: 1, properties: { _quiz_variant: variantId, _quiz_routine_step: v.routine.indexOf(r) + 1 } });
      } catch(_) {}
    }
    if (items.length === 0) { alert("Hmm — couldn't load your routine. Please refresh and try again."); return; }
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error("cart add failed");
      window.location.href = "/cart";
    } catch (e) {
      console.error("[laku-quiz] add to cart failed:", e);
      alert("Couldn't add to cart. Please try adding products individually.");
    }
  }

  async function hydrateProducts(node, routine) {
    for (const r of routine) {
      try {
        const res = await fetch(`/products/${r.handle}.js`);
        if (!res.ok) continue;
        const p = await res.json();
        const card = $(`[data-handle="${r.handle}"]`, node);
        if (!card) continue;
        const img = $(".lq-result__product-img", card);
        if (img && p.featured_image) { img.src = p.featured_image; img.alt = p.title; }
        const name = $(".lq-result__product-name", card);
        if (name) name.textContent = p.title;
        const price = $(".lq-result__product-price", card);
        if (price) price.textContent = (p.price / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
        card.addEventListener("click", (e) => {
          if (e.target.closest("button")) return;
          window.location.href = p.url;
        });
        card.style.cursor = "pointer";
      } catch(_) {}
    }
  }

  function render() {
    if (!root) return;
    root.innerHTML = "";
    if (state.step < QUESTIONS.length) root.appendChild(renderQuestion(state.step));
    else if (state.step === QUESTIONS.length) root.appendChild(renderEmail());
    else root.appendChild(renderResult());
  }

  function boot() {
    root = document.querySelector("[data-laku-quiz-root]");
    if (!root) return;
    restore();
    render();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
